import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router";
import ReviewDraftList from "@/modules/review/components/review-draft-list";
import { useReviews } from "@/modules/review/hooks/use-reviews";
import { useAuth } from "@/modules/auth";
import type { Review } from "@/modules/review/types";
import { ReviewListSkeleton } from "@/modules/review";
import Container from "@/components/ui/container";

export default function DraftReviewsPage() {
	const navigate = useNavigate();
	const { brandUrl } = useParams();
	const { currentWorkspaceId } = useAuth();
	const { indexDrafts } = useReviews();

	const [draftReviews, setDraftReviews] = useState<Review[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [draftNextCursor, setDraftNextCursor] = useState<string | null>(null);
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const loadDrafts = useCallback(
		async (cursor?: string | null) => {
			if (!currentWorkspaceId) return;

			if (cursor) {
				setIsLoadingMore(true);
			} else {
				setIsLoading(true);
			}
			setError(null);

			try {
				const response = await indexDrafts(currentWorkspaceId, cursor);
				if (response) {
					if (cursor) {
						setDraftReviews((prev) => [...prev, ...response.data]);
					} else {
						setDraftReviews(response.data);
					}
					setDraftNextCursor(response.meta.next_cursor);
				} else {
					setError("Failed to load incomplete submissions");
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : "An error occurred while loading incomplete submissions");
			} finally {
				setIsLoading(false);
				setIsLoadingMore(false);
			}
		},
		[indexDrafts, currentWorkspaceId],
	);

	useEffect(() => {
		loadDrafts();
	}, [loadDrafts]);

	const handleLoadMoreDrafts = () => {
		if (draftNextCursor && !isLoadingMore) {
			loadDrafts(draftNextCursor);
		}
	};

	const handleViewDraft = (id: string) => {
		navigate(`/${brandUrl}/reviews/${id}/draft`);
	};

	const handleBack = () => {
		navigate(`/${brandUrl}/reviews`);
	};

	return (
		<Container className="py-12">
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-center justify-between gap-8">
					<div className="flex items-center gap-4">
						<button onClick={handleBack} className="flex items-center gap-2 text-tertiary hover:text-primary transition-colors">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
							</svg>
							<span>Back to Reviews</span>
						</button>
						<div className="w-px h-6 bg-gray-200" />
						<div>
							<h1 className="text-xl font-semibold text-primary">Incomplete Submissions</h1>
						</div>
					</div>
				</div>

				{/* Content */}
				<div>
					{isLoading ? (
						<ReviewListSkeleton />
					) : error ? (
						<div className="bg-red-50 border border-red-200 rounded-md p-4">
							<p className="text-sm text-red-600">{error}</p>
							<button onClick={() => loadDrafts()} className="mt-2 text-sm font-medium text-red-600 hover:text-red-700 underline">
								Try again
							</button>
						</div>
					) : (
						<>
							<ReviewDraftList reviews={draftReviews} onClick={handleViewDraft} isLoading={false} />
							{!isLoading && !error && draftNextCursor && (
								<div className="flex justify-center mt-8">
									<button
										type="button"
										onClick={handleLoadMoreDrafts}
										disabled={isLoadingMore}
										className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-tertiary bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
									>
										{isLoadingMore ? (
											<>
												<svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
													<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
													<path
														className="opacity-75"
														fill="currentColor"
														d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
													/>
												</svg>
												Loading...
											</>
										) : (
											"Load More"
										)}
									</button>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</Container>
	);
}
